import { storage } from '@uhg-abyss/web/tools/storage';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { Constants } from '../common/Constants';

/**
 * Mock Reciprocity Context
 * Allows QAs to test Behavioral Coverage Provider Search by overriding reciprocity IDs
 * based on coverage type matching
 */

export interface MockReciprocityConfig {
  // QA selected coverage type for mocking (M, B, D, V)
  selectedCoverageType: string;
  // Mocked reciprocity ID value
  mockedReciprocityId: string;
  // Whether mocking is enabled
  isEnabled: boolean;
}

interface MockReciprocityContextType {
  config: MockReciprocityConfig;
  setSelectedCoverageType: (coverageType: string) => void;
  setMockedReciprocityId: (id: string) => void;
  setIsEnabled: (enabled: boolean) => void;
  getMockedReciprocityId: (payloadCoverageType: string) => string | null;
  clearMockData: () => void;
}

const MockReciprocityContext = createContext<
  MockReciprocityContextType | undefined
>(undefined);

const MOCK_RECIPROCITY_STORAGE_KEY =
  Constants.STORAGE_KEYS.SESSION.MOCK_RECIPROCITY_CONFIG;

export const DEFAULT_CONFIG: MockReciprocityConfig = {
  selectedCoverageType: '',
  mockedReciprocityId: '',
  isEnabled: false,
};

export const MockReciprocityProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [config, setConfig] = useState<MockReciprocityConfig>(DEFAULT_CONFIG);
  const [isHydrated, setIsHydrated] = useState(false);

  // Initialize from session storage on mount
  useEffect(() => {
    const storedConfig = storage.session.get(MOCK_RECIPROCITY_STORAGE_KEY);
    if (storedConfig) {
      setConfig(storedConfig);
    }
    setIsHydrated(true);
  }, []);

  // Persist to session storage whenever config changes
  useEffect(() => {
    if (isHydrated) {
      storage.session.set(MOCK_RECIPROCITY_STORAGE_KEY, config);
    }
  }, [config, isHydrated]);

  const setSelectedCoverageType = (coverageType: string) => {
    setConfig((prev) => ({
      ...prev,
      selectedCoverageType: coverageType,
    }));
  };

  const setMockedReciprocityId = (id: string) => {
    setConfig((prev) => ({
      ...prev,
      mockedReciprocityId: id,
    }));
  };

  const setIsEnabled = (enabled: boolean) => {
    setConfig((prev) => ({
      ...prev,
      isEnabled: enabled,
    }));
  };

  /**
   * Returns the mocked reciprocity ID if:
   * 1. Mocking is enabled
   * 2. A mocked ID has been provided
   * 3. The payload coverage type matches the selected coverage type
   * Otherwise returns null
   */
  const getMockedReciprocityId = (
    payloadCoverageType: string
  ): string | null => {
    if (
      !config.isEnabled ||
      !config.mockedReciprocityId ||
      !config.selectedCoverageType
    ) {
      return null;
    }

    // Only return mock if coverage types match
    if (payloadCoverageType === config.selectedCoverageType) {
      return config.mockedReciprocityId;
    }

    return null;
  };

  const clearMockData = () => {
    setConfig(DEFAULT_CONFIG);
  };

  const value: MockReciprocityContextType = {
    config,
    setSelectedCoverageType,
    setMockedReciprocityId,
    setIsEnabled,
    getMockedReciprocityId,
    clearMockData,
  };

  return (
    <MockReciprocityContext.Provider value={value}>
      {children}
    </MockReciprocityContext.Provider>
  );
};

export const useMockReciprocity = (): MockReciprocityContextType | null => {
  const context = useContext(MockReciprocityContext);
  if (!context) {
    throw new Error(
      'useMockReciprocity must be used within MockReciprocityProvider'
    );
  }
  return context;
};
